import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AvatarInput, PLATFORM_THEME_COLORS, Typography } from '@thinknet-gmbh/platform-common-ui';
import { PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { displayAddress, formatByMask, getTimezoneValue } from 'src/common/helpers/common.helpers';
import { useUploadCompanyLogoById } from 'src/hooks/companies/upload-company-logo.hook';
import { useUserCanManageDocument } from 'src/hooks/user-can-manage-document.hook';

import {
  MainContainer,
  InfoBlock,
  InfoElement,
  InfoWrapper,
  AddressWrapper,
  GridWrapper,
} from './company-profile-info.styles';

const CompanyProfileInfo = ({ company }: { company: any }) => {
  const [logo, setLogo] = useState<File | string | undefined>(company.logoUrl);
  const { t } = useTranslation(['common', 'settings']);
  const { canEdit } = useUserCanManageDocument({
    createdBy: company.createdBy,
    platformPermission: PLATFORM_PERMISSIONS.companyManagement,
  });

  const changeCompanyLogo = useUploadCompanyLogoById();

  const onLogoChangeHandler = (logo: File | undefined) => {
    setLogo(logo);
    changeCompanyLogo({ variables: { logo, companyId: company._id } });
  };

  return (
    <MainContainer>
      <AvatarInput
        onChange={onLogoChangeHandler}
        avatar={logo}
        btnText={t('common:buttons.uploadLogo')}
        hasPermission={canEdit}
        squareForm={true}
      />
      <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('settings:company.labels.generalInformation')}
      </Typography>
      <GridWrapper>
        {company.parentCompanyId && (
          <InfoElement>
            <Typography weight="bold">{`${t(
              'settings:company.labels.parentCompany'
            )}:`}</Typography>
            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
              {company.parentCompany?.name}
            </Typography>
          </InfoElement>
        )}
        <InfoElement>
          <Typography weight="bold">{`${t('settings:company.labels.companyName')}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>{company.name}</Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{t('settings:datev.labels.datevNumber')}:</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {company?.datevNumber?.datevNumber ? `№${company.datevNumber.datevNumber}` : '-'}
          </Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t(
            'settings:company.labels.companyPhoneNumber'
          )}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {formatByMask(company.phoneNumber, '+## ### ### #######')}
          </Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t('settings:company.labels.timezone')}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {getTimezoneValue(company)}
          </Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t(
            'settings:company.labels.companyEmailAddress'
          )}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>{company.email}</Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t('settings:company.labels.ceo')}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {company.ceo || '-'}
          </Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t(
            'settings:company.labels.companyMainAddress'
          )}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {[
              company.mainStreet && company.mainHouseNumber
                ? `${company.mainStreet} ${company.mainHouseNumber}`
                : company.mainStreet || company.mainHouseNumber,
              company.mainZip && company.mainCity
                ? `${company.mainZip} ${company.mainCity}`
                : company.mainZip || company.mainCity,
            ]
              .filter(Boolean)
              .join(', ') || '-'}
          </Typography>
        </InfoElement>
      </GridWrapper>
      {company.locations.length > 0 && (
        <>
          <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('common:labels.location')}
          </Typography>
          {company?.locations?.map((item: any, index: number) => (
            <InfoWrapper key={index}>
              <div>
                <InfoElement>
                  <Typography weight="bold">{`${t(
                    'settings:holidayManager.labels.locationName'
                  )}:`}</Typography>
                  <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                    {item.name}
                  </Typography>
                </InfoElement>
                <InfoElement>
                  <Typography weight="bold">{t('settings:datev.labels.datevNumber')}:</Typography>
                  <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                    {item.datevNumber ? ` №${item.datevNumber.datevNumber}` : '-'}
                  </Typography>
                </InfoElement>
                <InfoElement>
                  <Typography weight="bold">{`${t(
                    'settings:company.labels.companyNumber'
                  )}:`}</Typography>
                  <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                    {item?.companyNumber ? `${item.companyNumber}` : '-'}
                  </Typography>
                </InfoElement>
              </div>

              <div>
                {item.businessAddresses.map((address: any, idx: number) => (
                  <AddressWrapper key={idx}>
                    <div>
                      <InfoElement>
                        <Typography weight="bold">{`${t(
                          'common:labels.businessAddress'
                        )}:`}</Typography>
                        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                          {displayAddress(address)}
                        </Typography>
                      </InfoElement>
                      <InfoElement>
                        <Typography weight="bold">{`${t(
                          'settings:holidayManager.labels.holidayLocation'
                        )}:`}</Typography>
                        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                          {address.holidayLocation?.name}
                        </Typography>
                      </InfoElement>
                    </div>
                    <AddressWrapper>
                      {address.departments.map((department: any, key: number) => (
                        <InfoBlock key={key}>
                          <InfoElement>
                            <Typography weight="bold">{`${t(
                              'settings:userProfileSettings.labels.department'
                            )}:`}</Typography>
                            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                              {department.name}
                            </Typography>
                          </InfoElement>
                          <InfoElement>
                            <Typography weight="bold">{`${t(
                              'employee:labels.costCenter'
                            )}:`}</Typography>
                            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                              {department.costCenter}
                            </Typography>
                          </InfoElement>
                        </InfoBlock>
                      ))}
                    </AddressWrapper>
                  </AddressWrapper>
                ))}
              </div>
            </InfoWrapper>
          ))}
        </>
      )}
    </MainContainer>
  );
};

export default CompanyProfileInfo;
